import { untilDestroyed } from '@ngneat/until-destroy';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
  dateRangeValidator,
  DateTimeUtility,
  EnumToKeyValueArray,
  GetTeamId,
  GetUserId,
  isMobileView,
  removeNullAndUndefinedProperties,
  stripHtml,
  timeRangeValidator,
  uniqueFieldValidator,
} from '@core/utils';
import { DropDownItem } from '@core/types/Dropdown.type';
import { FormService, Logger } from '@core/services';
import { ChangeDetectorRef, ElementRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef, DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { ConfirmationDialogType, DeleteConfirmationOptions, EventType, NotificationType, RecurrenceOption, RecurrenceType, SocketEvents, TagType, TaskType, Visibility } from '@core/enums';
import { RecurrenceDatesFormComponent } from '../components/forms/recurrence-dates-form/recurrence-dates-form.component';
import { UseClub, UseEvent, UseEventVenue, UseGroups, UseTag } from '@core/usecases';
import { EventEntity, EventTask, EventTaskUser, GroupEntity, GroupParticipant, RecurringDate, RoomEntity, TaskCollaborator, UserEntity } from '@core/entities';
import { PermissionService } from '@auth';
import { instanceToPlain } from 'class-transformer';
import { HotToastService } from '@ngxpert/hot-toast';
import { Router } from '@angular/router';
import { RRule } from 'rrule';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { WorkShiftFormComposition } from '@shared/compositions';
import { saveAs } from 'file-saver';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';

const log = new Logger('Events Form Composition');

export class EventFormComposition extends FormService {
  selectedImage: File;
  days: DropDownItem[] = [];
  showEndDate = false;
  ShowRepeatEvent = false;
  taskTypes: any[] = [];

  tags: DropDownItem[] = [];
  eventVenues: DropDownItem[] = [];
  groups: GroupEntity[] = [];
  rooms: RoomEntity[] = [];
  users: UserEntity[] = [];
  repeatEvent: any[] = [];
  eventTypes: any[] = [];
  visibilityOfEvent: any[] = [];

  selectedAttachment: File;

  // protected eventTypes = EventType;
  showAssignTaskForm = false;

  // For UPDATE
  eventId: number;

  loadings = {
    groups: true,
    downloadingDocument: false,
    deletingDocument: false,
  };
  protected _currentSelectedEvent: EventEntity;
  protected readonly _translateService = inject(TranslateService);
  protected _dialogService: DialogService;
  protected _dialogRef: DialogRef;
  protected _bottomSheetService: NgxBottomSheetService;
  private _groupParticipants: GroupParticipant[] = [];
  private _taskGroupParticipants: GroupParticipant[] = [];
  private readonly _permissionService = inject(PermissionService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _elRef = inject(ElementRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _toast = inject(HotToastService);
  private readonly _router = inject(Router);
  private readonly _useTags = new UseTag();
  private readonly _useClub = new UseClub();
  private readonly _useEvent = new UseEvent();
  private readonly _useEventVenue = new UseEventVenue();
  private readonly _useGroup = new UseGroups();
  private readonly _workShiftComposition = new WorkShiftFormComposition();
  private readonly _socketService = inject(SocketIoService);

  constructor() {
    super();
    this.days = DateTimeUtility.getWeekDays(this._translateService) as DropDownItem[];
    this.repeatEvent = EnumToKeyValueArray(RecurrenceType, this._translateService);
    this.eventTypes = EnumToKeyValueArray(EventType, this._translateService).filter((type) => type.value !== EventType.AllEvents);

    // remove course and seminar from event types as requested
    this.eventTypes = this.eventTypes.filter((type) => type.value !== EventType.Course && type.value !== EventType.Seminar);

    this.visibilityOfEvent = EnumToKeyValueArray(Visibility, this._translateService);
    this.taskTypes = EnumToKeyValueArray(TaskType, this._translateService);

    if (!isMobileView()) {
      this._dialogService = inject(DialogService);
      this._dialogRef = inject(DialogRef);
      this._currentSelectedEvent = this._dialogRef.data?.event;
    } else {
      this._bottomSheetService = inject(NgxBottomSheetService);
      this._currentSelectedEvent = this._bottomSheetService.currentSheetData.event;
    }

    this._initDataForForm();

    if (this._currentSelectedEvent) {
      this.eventId = this._currentSelectedEvent.id;
      this._fetchEventDetails(this._currentSelectedEvent.id);
    } else {
      this._fetchEventVenues();
    }
  }

  get currentSelectedEvent(): EventEntity {
    return this._currentSelectedEvent;
  }

  get eventDateFormArray() {
    return this.form.get('eventDate') as FormArray;
  }

  get workShiftsFormArray(): FormArray {
    return this.form.get('eventShifts') as FormArray;
  }

  get allDaysSelected() {
    return this.eventDateFormArray.length === this.days.length;
  }

  get isSingleValidTimeTo(): boolean {
    const eventDateArray = this.eventDateFormArray;

    // Check if there is exactly one item in the form array
    if (eventDateArray.length === 1) {
      const timeToControl = eventDateArray.at(0).get('endTime');

      // Check if the endTime control exists, has a value, and is valid
      return !!timeToControl && timeToControl.value && timeToControl.valid;
    }

    return false;
  }

  get isSingleValidDateFrom(): boolean {
    const eventDateArray = this.eventDateFormArray;

    // Check if there is exactly one item in the form array
    if (eventDateArray.length === 1) {
      const dateFromControl = eventDateArray.at(0).get('dateFrom');

      // Check if the dateFrom control exists, has a value, and is valid
      return !!dateFromControl && dateFromControl.value && dateFromControl.valid;
    }

    return false;
  }

  get canAddWorkShifts(): boolean {
    return this._permissionService.isCourseAccessAllowed();
  }

  async downloadDocument(event: EventEntity) {
    if (event.document) {
      this.loadings.downloadingDocument = true;
      try {
        const filePath = event.document;
        const fileName = filePath.split('/').pop();

        const response = await this._useEvent.downloadDocument(event.id, filePath).toPromise();
        const blob = new Blob([response], { type: 'application/octet-stream' });

        // Use FileSaver to save the file
        saveAs(blob, fileName || 'event-document');
        this.loadings.downloadingDocument = false;
      } catch (error) {
        log.error('Error downloading the document:', error);
        this._toast.error(this._translateService.instant('Something went wrong'));
        this.loadings.downloadingDocument = false;
      }
    } else {
      log.error('Event document URL or title is missing.');
      this.loadings.downloadingDocument = false;
    }
  }

  addWeekdaysRow(dayData?: RecurringDate) {
    const eventDate = this.eventDateFormArray;
    const newGroup = this._formBuilder.group(
      {
        dateFrom: [dayData?.dateFrom, [Validators.required]],
        dateEnd: [dayData?.dateEnd || null, [Validators.required]],
        startTime: [dayData?.startTime || null, [Validators.required]],
        endTime: [dayData?.endTime || null, [Validators.required]],
      },
      { validators: [timeRangeValidator('startTime', 'endTime'), dateRangeValidator('dateFrom', 'dateEnd')] },
    );

    if (!dayData) {
      newGroup
        .get('dateFrom')
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe({
          next: (date) => {
            if (date) {
              const dateEndControl = newGroup.get('dateEnd');
              if (dateEndControl) {
                dateEndControl.setValue(date, { emitEvent: false });
              }
            }
          },
        });
    }

    eventDate.push(newGroup);

    eventDate.controls.forEach((group) => {
      const dayControl = group.get('dateFrom');
      if (dayControl) {
        dayControl.setValidators([Validators.required, uniqueFieldValidator(eventDate, 'dateFrom')]);
        dayControl.updateValueAndValidity({ emitEvent: false });
        dayControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  removeWeekdaysRow(index: number) {
    const eventDate = this.form.get('eventDate') as FormArray;
    eventDate.removeAt(index);
  }

  selectAllParticipants(taskSelector = false) {
    const controlName = taskSelector ? 'task.userSelect' : 'participants';
    this.form.get(controlName).setValue(this.users.map((user) => user.id));
  }

  unSelectAll(taskSelector = false) {
    const controlName = taskSelector ? 'task.userSelect' : 'participants';
    this.form.get(controlName).setValue([]);
  }

  addWorkShiftRow() {
    if (this.workShiftsFormArray.length && this.workShiftsFormArray.invalid) {
      this.workShiftsFormArray.markAllAsTouched();
      return;
    }
    this.workShiftsFormArray.push(this._workShiftComposition.createShift());
  }

  removeWorkShiftRow(index: number) {
    this.workShiftsFormArray.removeAt(index);
  }

  close() {
    if (!isMobileView()) {
      this._dialogRef.close();
    } else {
      this._bottomSheetService.close();
    }
  }

  save(btnRef?: ButtonComponent) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form, this._elRef);

      for (const key in this.form.controls) {
        if (this.form.controls[key].errors) {
          log.error(key, this.form.controls[key].errors);
        }
      }

      return;
    }

    const formValues = {
      ...this.form.value,
      groupId: this.form.get('groupId')?.value,
      type: this.form.get('type')?.value,
      visibility: this.form.get('visibility')?.value,
    } as EventEntity;

    formValues.author = +GetUserId();
    formValues.approvedStatus = this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? 1 : 0;
    formValues.dateFrom = formValues.eventDate[0].dateFrom + ' ' + formValues.eventDate[0].startTime;
    formValues.startTime = formValues.eventDate[0].startTime;
    formValues.endTime = formValues.eventDate[formValues.eventDate.length - 1].endTime;

    if (!this.form.get('repeatEvent')?.value || this.form.get('repeatEvent')?.value === RecurrenceType.DoesNotRepeat || formValues.eventDate.length > 1) {
      formValues.dateTo = formValues.eventDate[formValues.eventDate.length - 1].dateFrom + ' ' + formValues.eventDate[formValues.eventDate.length - 1].endTime;
      formValues.dateRepeat = formValues.dateTo;
      formValues.recurrence = null;
    } else {
      formValues.dateTo = this.form.get('repeatEndDate')?.value + ' ' + formValues.eventDate[formValues.eventDate.length - 1].endTime;
      formValues.dateRepeat = this.form.get('repeatEndDate')?.value + ' ' + formValues.eventDate[formValues.eventDate.length - 1].endTime;

      if (this.form.get('repeatEvent')?.value !== RecurrenceType.Custom) {
        const repeatOption = this.form.get('repeatEvent')?.value;
        const startDate = new Date(
          Date.UTC(new Date(formValues.eventDate[0].dateFrom).getFullYear(), new Date(formValues.eventDate[0].dateFrom).getMonth(), new Date(formValues.eventDate[0].dateFrom).getDate()),
        );
        // Setting UNTIL to UTC start of the specified end date
        const endDate = new Date(
          Date.UTC(
            new Date(this.form.get('repeatEndDate')?.value).getFullYear(),
            new Date(this.form.get('repeatEndDate')?.value).getMonth(),
            new Date(this.form.get('repeatEndDate')?.value).getDate(),
          ),
        );

        let freq, byday;
        switch (repeatOption) {
          case RecurrenceOption.RepeatDaily:
            freq = RRule.DAILY;
            break;
          case RecurrenceOption.RepeatWeekly:
            freq = RRule.WEEKLY;
            byday = RRule.MO; // Adjust based on actual day needed
            break;
          case RecurrenceOption.RepeatMonthly:
            freq = RRule.MONTHLY;
            break;
          case RecurrenceOption.RepeatYearly:
            freq = RRule.YEARLY;
            break;
          default:
            freq = RRule.WEEKLY;
            break;
        }

        const rruleOptions = {
          freq,
          dtstart: startDate,
          until: endDate,
          byweekday: byday ? [byday] : undefined,
          wkst: RRule.SU,
        };

        const rrule = new RRule(rruleOptions);
        let rruleString = rrule.toString();
        rruleString = `RRULE:${rruleString.match(/FREQ=[^;]+;[^;]+/)[0]};DTSTART=${startDate.toISOString().replace(/[-:]/g, '').replace('.000Z', 'Z')}`;

        formValues.recurrence = rruleString;
      } else {
        if (!formValues.recurrence) {
          this.form.get('repeatEvent')?.setValue(null);
          this.markAllInvalidControlsAndScrollToFirst(this.form, this._elRef);
        }
        formValues.recurrence = formValues.recurrence ? formValues.recurrence + ';UNTIL=' + DateTimeUtility.formatDateToUTCString(this.form.get('repeatEndDate')?.value) : null;
      }
    }
    /**
     * Participants related data
     */

    if (+formValues.visibility === Visibility.Group && formValues.groupId) {
      formValues.users = this._groupParticipants.map((participant) => ({
        userId: +participant.userId,
        approvedStatus: +participant.userId === +GetUserId() ? 1 : 0,
      }));
    } else if (+formValues.visibility === Visibility.Club) {
      formValues.users = this.users.map((user) => ({
        userId: +user.id,
        approvedStatus: +user.id === +GetUserId() ? 1 : 0,
      }));
    } else {
      if (formValues.participants && formValues.participants.length) {
        formValues.users = this.users
          .filter((user) => (formValues.participants as any).includes(+user.id))
          .map((user) => ({
            userId: +user.id,
            approvedStatus: +user.id === +GetUserId() ? 1 : 0,
          }));
      }
    }

    // const currentUser = formValues.users?.find((user) => user.userId === +GetUserId());
    // if (!currentUser) {
    //   if (formValues.users && formValues.users.length) {
    //     formValues.users.push({
    //       userId: +GetUserId(),
    //       approvedStatus: 1,
    //     });
    //   } else {
    //     formValues.users = [
    //       {
    //         userId: +GetUserId(),
    //         approvedStatus: 1,
    //       },
    //     ];
    //   }
    // }

    formValues.chargeable = formValues.pricePerParticipant;
    formValues.createdBy = +GetUserId();
    formValues.teamId = +GetTeamId();

    /**
     * Task related data
     */
    if (formValues.isTask) {
      if (formValues.task.type === TaskType.Group) {
        formValues.task.taskCollaborators = this._taskGroupParticipants.map(
          (user) =>
            ({
              userId: user.userId,
              approvedStatus: 1,
            }) as unknown as TaskCollaborator,
        );

        formValues.task.userSelect = this._taskGroupParticipants
          .filter((user) => user.userId !== +GetUserId())
          .map(
            (user) =>
              ({
                id: user.userId,
                username: user.groupUser[0].fullName,
              }) as EventTaskUser,
          );

        formValues.task.groups = [
          {
            id: formValues.groupId,
            name: this.groups.find((group) => group.id === formValues.groupId)?.name,
          },
        ];

        formValues.task.organizerId = +GetUserId();
      } else {
        const taskUsers = (formValues.task as unknown as EventTask).userSelect;
        formValues.task.taskCollaborators = taskUsers.map(
          (user) =>
            ({
              userId: user,
              approvedStatus: 1,
            }) as unknown as TaskCollaborator,
        );
        formValues.task.groups = '';
        formValues.task.groupId = 0;

        formValues.task.userSelect = this.users.filter((user) => (formValues.task.userSelect as any).includes(+user.id)).map((user) => ({ id: +user.id, username: user.fullName }) as EventTaskUser);
      }

      const currentUser = formValues.task.taskCollaborators.find((user) => user.userId === +GetUserId());
      if (!currentUser) {
        formValues.task.taskCollaborators.push({
          userId: +GetUserId(),
          approvedStatus: 1,
        });
      }
    } else {
      formValues.task = null;
    }

    const body = removeNullAndUndefinedProperties(instanceToPlain(new EventEntity(formValues)));

    body.task = [body.task];

    const formData = this.jsonToFromData(body, ['event_tags', 'users', 'eventDate', 'task']);

    if (formValues.visibility === Visibility.Public || formValues.visibility === Visibility.Private || formValues.visibility === Visibility.Group) {
      if (formValues.participants && formValues.participants.length) {
        // append participant value to form data as participant[0], participant[1], ...
        (formValues.participants as unknown as number[]).forEach((participant, index) => {
          formData.append(`participant[${index}]`, participant.toString());
        });
      }
    }

    formData.append('room', 'null'); // REQUIRED
    formData.append('allowed_persons', 'null');

    if (this._currentSelectedEvent) {
      formData.append('roomBookingDates', '""');
      if (!this.selectedAttachment) formData.append('event_document', this._currentSelectedEvent?.document?.toString());
      if (!this.selectedImage) formData.append('event_image', this._currentSelectedEvent?.imageUrl?.toString());
    }

    if (+formValues.visibility === Visibility.Group && formValues.groupId) {
      formData.append('groups', `[${formValues.groupId}]`);
    }

    if (this.selectedImage) {
      formData.append('file', this.selectedImage);
    }

    if (!formValues.isTask) {
      formData.delete('task');
    }

    /**
     * Work Shifts related data
     */

    formData.delete('isWorkShift');
    formData.delete('eventShifts');
    formData.delete('eventshift');

    if (formValues.isWorkShift) {
      const shifts = formValues.eventShifts.map((shift: any) => {
        shift.shiftAssignUser = shift.shiftAssignUser.map((userId: number) => {
          const matchedUser = this.users.find((user) => user.id === userId);
          if (matchedUser) {
            return {
              id: matchedUser.id,
              name: matchedUser.fullName,
            } as UserEntity;
          }
          return null;
        });

        return this.removeNullObjects(shift);
      });

      const shiftsFormData = this._workShiftComposition.convertShiftsToFormDataAndAppend(shifts);

      shiftsFormData.forEach((value, key) => {
        formData.append(key, value);
      });
    }

    if (this.selectedAttachment) {
      formData.append('file', this.selectedAttachment);
    }

    const service$ = this._currentSelectedEvent ? this._useEvent.updateEvent(this._currentSelectedEvent.id, formData) : this._useEvent.createEvent(formData);

    this.call(service$, btnRef)
      .then((response) => {
        if (response) {
          if (!this._permissionService.isAdmin()) {
            this._toast.success(this._translateService.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
          } else {
            const message = this._currentSelectedEvent && this._currentSelectedEvent?.id ? 'Update Successful' : 'Created Successfully';
            this._toast.success(this._translateService.instant(message));
          }

          this._reloadCurrentRoute();
          if (!isMobileView()) {
            this._dialogService.closeAll();
          } else {
            this._bottomSheetService.closeAll();
          }
          this._socketService.emit(SocketEvents.SendNotification, NotificationType.Event);
        } else {
          this._toast.error(this._translateService.instant('Something went wrong'));
        }
      })
      .catch((err) => {
        this._toast.error(this.error, { duration: 5000 });
        log.error('Error while creating or updating event');
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  deleteFile(event: EventEntity) {
    if (!event || !event.id) {
      return;
    }

    this.loadings.deletingDocument = true;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translateService.instant('Image'),
        },
      });
      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this._deleteAttachedFile(event?.id);
              }
              break;
            default:
              this.loadings.deletingDocument = false;
              break;
          }
        },
      });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset',
          data: {
            type: ConfirmationDialogType.DELETE_DOCUMENT,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (confirmed: boolean) => {
            if (confirmed) {
              this._deleteAttachedFile(event?.id);
            } else {
              this.loadings.deletingDocument = false;
            }
          },
        });
    }
  }

  private _initDataForForm() {
    this._initForm();
    this._handleDatePreselection();
    this._fetchTags();
    this._fetchTeamAndGroups();
  }

  /**
   * Handles the preselection of date for the event creation.
   * This method checks if a date is provided in the dialog data and preselects it which is passed by Calendar empty cell click.
   * @private
   */
  private _handleDatePreselection() {
    const preselectDate = !isMobileView() ? this._dialogRef?.data?.preselectDate : null;

    if (preselectDate) {
      const preSelectDateForEventCreation: RecurringDate = {
        dateFrom: DateTimeUtility.formatDateToString(preselectDate, 'YYYY-MM-DD'),
        dateEnd: DateTimeUtility.formatDateToString(preselectDate, 'YYYY-MM-DD'),
        startTime: null,
        endTime: null,
      };
      this.addWeekdaysRow(preSelectDateForEventCreation);
    } else {
      this.addWeekdaysRow();
    }
  }

  private _initForm() {
    this.form = this._formBuilder.group({
      name: [null, [Validators.required]],
      place: [null, [Validators.required]],
      type: [null, [Validators.required]],
      dateFrom: [null], // handled in save method
      dateTo: [null], // handled in save method
      dateRepeat: [null], // handled in save method
      startTime: [null], // handled in save method
      endTime: [null], // handled in save method
      visibility: [null, [Validators.required]],
      recurrence: [null],
      showGuestList: [false],
      chargeable: [false],
      pricePerParticipant: [{ value: 0, disabled: true }],
      description: [null, [Validators.required]],
      participants: [[]],
      users: [null], // handled in save method
      author: [GetUserId()],
      approvedStatus: [0],
      groupId: [null],
      eventDate: this._formBuilder.array([]),
      eventTagsArray: [[]],
      image: [null],
      isTask: [false],
      task: this._formBuilder.group({
        title: [null],
        description: [null],
        room: [null],
        type: [null],
        date: [null],
        organizerId: [`${GetUserId()}`],
        taskCollaborators: [[]],
        groups: [[]],
        groupId: [null],
        status: ['1'],
        userSelect: [null],
      }),
      repeatEndDate: [null],
      repeatEvent: [null],
      isWorkShift: [false],
      eventShifts: this._formBuilder.array([]),
    });

    this._bindFormValueChanges();
  }

  private _bindFormValueChanges() {
    // Type of Event based changes
    this.form
      .get('type')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value && value === EventType.FunctionariesEvent) {
            this.visibilityOfEvent = this.visibilityOfEvent.filter((visibility) => visibility.value !== Visibility.Public);
          } else {
            this.visibilityOfEvent = EnumToKeyValueArray(Visibility, this._translateService);
          }
        },
      });

    // Visibility Of Event based changes
    this.form
      .get('visibility')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          switch (value) {
            case Visibility.Group:
              this.form.get('participants')?.setValue(null);
              this.form.get('participants')?.disable();
              this.form.get('participants')?.setValidators(null);
              this.form.get('participants')?.updateValueAndValidity();

              this.form.get('groups')?.enable();
              this.form.get('groups')?.setValidators([Validators.required]);
              this.form.get('groups')?.updateValueAndValidity();

              break;
            case Visibility.Public:
            case Visibility.Private:
              this.form.get('groupId')?.setValue(null);
              this.form.get('groups')?.setValue(null);
              this.form.get('groups')?.disable();
              this.form.get('groups')?.setValidators(null);
              this.form.get('groups')?.updateValueAndValidity();

              this.form.get('participants')?.enable();
              this.form.get('participants')?.setValidators(null);
              this.form.get('participants')?.updateValueAndValidity();

              break;
            default:
              this.form.get('participants')?.disable();
              this.form.get('participants')?.setValidators(null);
              this.form.get('participants')?.updateValueAndValidity();

              this.form.get('groupId')?.setValue(null);
              this.form.get('groups')?.setValue(null);
              this.form.get('groups')?.disable();
              this.form.get('groups')?.setValidators(null);
              this.form.get('groups')?.updateValueAndValidity();
              break;
          }

          if (value === Visibility.Public) {
            this.form.get('showGuestList')?.setValue(true, { emitEvent: false });
          } else {
            this.form.get('showGuestList')?.setValue(false, { emitEvent: false });
          }
        },
      });

    // Group selection based changes

    this.form
      .get('groupId')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchGroupParticipants(value);
          }
        },
      });

    // Chargeable based changes
    this.form
      .get('chargeable')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this.form.get('pricePerParticipant')?.enable();
            this.form.get('pricePerParticipant')?.setValidators([Validators.required]);
            this.form.get('pricePerParticipant')?.updateValueAndValidity();
          } else {
            this.form.get('pricePerParticipant')?.disable();
            this.form.get('pricePerParticipant')?.setValidators(null);
            this.form.get('pricePerParticipant')?.updateValueAndValidity();
          }
        },
      });

    // Subscribe to changes in eventDate to manage repeatEvent
    this.form
      .get('eventDate')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const eventDates = value || [];
          if (eventDates.length > 1) {
            // Disable repeatEvent, clear validators, and set value to null
            this.form.get('repeatEvent')?.setValue(null);
            this.form.get('repeatEvent')?.disable();
            this.form.get('repeatEvent')?.setValidators(null);
            this.form.get('repeatEvent')?.updateValueAndValidity();
            this.showEndDate = false;
            this.form.get('repeatEndDate')?.setValue(null);
            this.form.get('repeatEndDate')?.disable();
            this.form.get('repeatEndDate')?.setValidators(null);
            this.form.get('repeatEndDate')?.updateValueAndValidity();
          } else {
            // Enable repeatEvent, add required validators, and retain current value
            this.form.get('repeatEvent')?.enable();
            this.form.get('repeatEvent')?.setValue(RecurrenceType.DoesNotRepeat);
            this.form.get('repeatEvent')?.setValidators([Validators.required]);
            this.form.get('repeatEvent')?.updateValueAndValidity();
          }
        },
      });

    // Subscribe to changes in repeatEvent to manage repeatEndDate
    this.form
      .get('repeatEvent')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value && value !== RecurrenceType.DoesNotRepeat) {
            // Show and enable repeatEndDate with validators when a repeating event is selected
            this.showEndDate = true;
            this.form.get('repeatEndDate')?.enable();
            this.form.get('repeatEndDate')?.setValidators([Validators.required, this._repeatEndDateValidator(this.form)]);
            this.form.get('repeatEndDate')?.updateValueAndValidity();

            if (value === RecurrenceType.Custom) {
              this._openCustomRecurrenceForm();
            }
          } else {
            // Hide, disable repeatEndDate, clear validators, and set value to null when no repeating event
            this.showEndDate = false;
            this.form.get('repeatEndDate')?.setValue(null);
            this.form.get('repeatEndDate')?.disable();
            this.form.get('repeatEndDate')?.setValidators(null);
            this.form.get('repeatEndDate')?.updateValueAndValidity();
          }
        },
      });

    // isTask based changes
    this.form
      .get('isTask')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (isTaskEnabled) => {
          const taskGroup = this.form.get('task') as FormGroup;

          // Commented because validation of task date and repeat end date was requested to be removed
          // const repeatEndDate = this.form.get('repeatEndDate') as FormControl;

          if (isTaskEnabled) {
            Object.keys(taskGroup.controls).forEach((field) => {
              if (field === 'title' || field === 'description' || field === 'type' || field === 'date' || field === 'groupId') {
                const control = taskGroup.get(field);
                control?.enable();

                // Apply general required validator to all fields in the task group
                control?.setValidators(Validators.required);

                // Specific case for the date field to include the custom date range validator
                if (field === 'date') {
                  // Commented because validation of task date and repeat end date was requested to be removed
                  // control?.setValidators([Validators.required, this._taskDateValidator(this.eventDateFormArray.controls, repeatEndDate)]);
                  control?.setValidators([Validators.required]);
                }

                control?.updateValueAndValidity();
              }
            });
          } else {
            Object.keys(taskGroup.controls).forEach((field) => {
              const control = taskGroup.get(field);
              control?.disable();
              control?.clearValidators();
              control?.updateValueAndValidity();
            });
          }
        },
      });

    // task.type based changes
    this.form
      .get('task.type')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value === TaskType.Group) {
            this.form.get('task.groupId')?.enable();
            this.form.get('task.groupId')?.setValidators([Validators.required]);
            this.form.get('task.groupId')?.updateValueAndValidity();

            this.form.get('task.userSelect')?.setValue(null);
            this.form.get('task.userSelect')?.disable();
            this.form.get('task.userSelect')?.clearValidators();
            this.form.get('task.userSelect')?.updateValueAndValidity();
          } else {
            this.form.get('task.groupId')?.setValue(null);
            this.form.get('task.groupId')?.disable();
            this.form.get('task.groupId')?.clearValidators();
            this.form.get('task.groupId')?.updateValueAndValidity();

            this.form.get('task.userSelect')?.enable();
            this.form.get('task.userSelect')?.setValidators([Validators.required]);
            this.form.get('task.userSelect')?.updateValueAndValidity();
          }
        },
      });

    this.form
      .get('task.groupId')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchGroupParticipants(value, true);
          }
        },
      });

    this.form
      .get('isWorkShift')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this.addWorkShiftRow();
          } else {
            this.workShiftsFormArray.clear();
          }
        },
      });
  }

  private _patchFormValues(event: EventEntity) {
    const participants = event.participants?.map((participant) => participant.userId).filter((id) => id !== +GetUserId());
    const tags = event.eventTags?.map((tag: any) => tag.tagName) ?? [];

    /**
     * Patch Main Event Form
     */
    this.form.patchValue({
      name: event.name,
      place: event.place,
      type: event.type,
      visibility: +event.visibility,
      description: stripHtml(event.description),
      chargeable: event.chargeable && +event.chargeable > 0,
      pricePerParticipant: event.chargeable ? event.chargeable : 0,
      participants,
      eventTagsArray: tags,
      image: event.imageUrl ? event.imageUrl : null,
      isTask: event.isTask,
      groupId: event.groupId,
      isWorkShift: event.isWorkShift,
    });

    /**
     * Patch Recurring Dates
     */

    if (event.recurringDates) {
      this.eventDateFormArray.clear();
      this.eventDateFormArray.reset();
      event.recurringDates.forEach((date) => {
        this.addWeekdaysRow(date as RecurringDate);
      });
    }

    /**
     * Patch Repeat Event based on Recurrence
     */

    if (event.recurrence) {
      try {
        const rrule = RRule.fromString(event.recurrence);

        if (event.recurrence.includes('INTERVAL')) {
          // If 'INTERVAL' is available in string then set to 'Custom'
          this.form.get('repeatEvent').setValue(RecurrenceType.Custom, { emitEvent: false });
          this.showEndDate = true;
          this.form.get('repeatEndDate')?.enable();
          this.form.get('repeatEndDate')?.setValidators([Validators.required, this._repeatEndDateValidator(this.form)]);
          this.form.get('repeatEndDate')?.updateValueAndValidity();
        } else {
          // Otherwise, map 'FREQ' to respective enum values
          switch (rrule.options.freq) {
            case RRule.DAILY:
              this.form.get('repeatEvent').setValue(RecurrenceType.EveryDay);
              break;
            case RRule.WEEKLY:
              this.form.get('repeatEvent').setValue(RecurrenceType.EveryWeek);
              break;
            case RRule.MONTHLY:
              this.form.get('repeatEvent').setValue(RecurrenceType.EveryMonth);
              break;
            case RRule.YEARLY:
              this.form.get('repeatEvent').setValue(RecurrenceType.EveryYear);
              break;
            default:
              this.form.get('repeatEvent').setValue(RecurrenceType.DoesNotRepeat);
          }
        }

        // Set 'repeatEndDate' if applicable
        if (rrule.options.until) {
          const endDate = new Date(rrule.options.until);
          this.form.get('repeatEndDate').setValue(endDate.toISOString().split('T')[0]); // Format YYYY-MM-DD
        }
      } catch {
        this.form.get('repeatEvent').setValue(RecurrenceType.DoesNotRepeat);
      }
    } else {
      // If no recurrence, set to 'Does Not Repeat'
      this.form.get('repeatEvent').setValue(RecurrenceType.DoesNotRepeat);
    }

    /**
     * Patch Task Form
     */

    if (event.isTask && event.eventTask) {
      const task = event.eventTask;

      const taskType = task.groupId ? TaskType.Group : TaskType.Individual;
      // Filter out the current user's ID from collaborators
      const taskCollaborators = task.collaborators?.map((collaborator) => collaborator.userId).filter((id) => id !== +GetUserId());

      this.form.patchValue({
        task: {
          title: task.title,
          description: task.description,
          type: taskType,
          groupId: +task.groupId,
          userSelect: taskCollaborators,
          status: task.status,
          organizerId: task.organizerId,
          // format date in only date format "YYYY-MM-DD"
          date: task.date.split('T')[0],
        },
      });
    }

    /**
     * Patch Work Shifts Form
     */

    if (event?.eventShifts && event.eventShifts.length) {
      this.workShiftsFormArray.clear();

      event?.eventShifts.forEach((shift) => {
        const users = shift.shiftAssign.map((user) => user.userId);
        this.workShiftsFormArray.push(
          this._formBuilder.group({
            id: [shift.id],
            date: [DateTimeUtility.getDateOnly(shift.date), [Validators.required]],
            startTime: [shift.startTime, [Validators.required]],
            endTime: [shift.endTime, [Validators.required]],
            title: [shift.title, [Validators.required]],
            desc: [shift.description, [Validators.required]],
            image: [shift.imageUrl],
            shiftAssignUser: [users],
          }),
        );
      });
    }
  }

  private _openCustomRecurrenceForm() {
    if (this.eventDateFormArray.invalid) {
      this._toast.error(this._translateService.instant('Please enter event date'));

      this.eventDateFormArray.markAllAsTouched();
      this.form.get('repeatEvent')?.setValue(null);
      return;
    }

    const recurringForm = isMobileView()
      ? this._bottomSheetService.open(RecurrenceDatesFormComponent, {
          height: '60%',
          showCloseButton: false,
          data: {
            dateStart: this.eventDateFormArray.at(0).get('dateFrom')?.value,
            currentRecurrence: this._currentSelectedEvent?.recurrence?.includes('INTERVAL') ? this._currentSelectedEvent?.recurrence : null,
          },
        })
      : this._dialogService.open(RecurrenceDatesFormComponent, {
          width: '500px',
          windowClass: 'dialog-overflow-unset',
          data: {
            dateStart: this.eventDateFormArray.at(0).get('dateFrom')?.value,
            currentRecurrence: this._currentSelectedEvent?.recurrence?.includes('INTERVAL') ? this._currentSelectedEvent?.recurrence : null,
          },
        });

    recurringForm.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        if (result && result.trim() !== '') {
          this.form.get('recurrence')?.setValue(result);
        } else {
          if (!this._currentSelectedEvent || !this._currentSelectedEvent.recurrence.includes('INTERVAL')) {
            this.form.get('repeatEvent')?.setValue(null);
          }
        }
      },
    });
  }

  private _fetchEventDetails(eventId: number) {
    this._useEvent
      .getAEvent(eventId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            this._currentSelectedEvent = res;
            this._patchFormValues(res);

            // to handle missing event venues for older records
            this._fetchEventVenues();
          }
        },
      });
  }

  private _fetchTags() {
    this._useTags
      .getAllTagsByType(TagType.EVENT)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.tags = res.map((tag) => ({ label: tag.tagName, value: tag.tagName }));
          }
        },
      });
  }

  private _fetchEventVenues() {
    this._useEventVenue
      .getAllEventVenues()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.eventVenues = res.map((venue) => ({ name: venue.venue, value: venue.venue }));
          }

          // if the form in is update mode and the current selected event has a place that is not in the list of venues, add it to the list
          // this is to ensure backwards compatibility for older records with custom places not available in the venue list
          if (this.currentSelectedEvent) {
            const venueAvailable = this.eventVenues.find((venue) => venue.value === this.currentSelectedEvent.place);

            if (!venueAvailable) {
              if (this.eventVenues && this.eventVenues.length) {
                this.eventVenues.push({
                  name: this.currentSelectedEvent.place,
                  value: this.currentSelectedEvent.place,
                });
              } else {
                this.eventVenues = [
                  {
                    name: this.currentSelectedEvent.place,
                    value: this.currentSelectedEvent.place,
                  },
                ];
              }
            }
          }
        },
      });
  }

  private _fetchTeamAndGroups() {
    this._useClub
      .getClubGroupsRoomsAndUsers()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            this.groups = res.groups;
            this.rooms = res.rooms;
            this.users = res.users;
          }
        },
      });
  }

  private _fetchGroupParticipants(groupId: number, isTaskGroup = false) {
    this._useGroup
      .getApprovedGroupUsers(groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          if (res) {
            if (isTaskGroup) {
              this._taskGroupParticipants = res.participants;
              log.info('Task Group Participant set');
            } else {
              this._groupParticipants = res.participants;
              log.info('Group Participant set');
            }
          }
        },
      });
  }

  private _deleteAttachedFile(eventId: number) {
    if (!eventId) {
      return;
    }

    this._useEvent
      .deleteEventFile(eventId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          if (response) {
            this._toast.success(this._translateService.instant('Delete Successful'));
            this._fetchEventDetails(eventId);
          }
          this.loadings.deletingDocument = false;
        },
        error: (error) => {
          this._toast.error(error?.error?.message ?? this._translateService.instant('Something went wrong'));
          log.error('Error deleting file:', error);
          this.loadings.deletingDocument = false;
        },
      });
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    const urlTree = this._router.parseUrl(currentUrl);

    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigateByUrl(urlTree);
    });
  }

  /**
   * Validates that the task date is within the specified range of event dates.
   * If `repeatEndDate` is specified, it checks between the first event date and the repeat end date.
   * Otherwise, it checks between the first and the last event date.
   * All dates are expected to be strings in the format "YYYY-MM-DD".
   * @param eventDateArray
   * @param repeatEndDate
   * @private
   */
  private _taskDateValidator(eventDateArray: AbstractControl[], repeatEndDate: AbstractControl | null): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // If no date is set, no need to validate
      }

      const taskDate = new Date(control.value);
      const firstEventDate = eventDateArray.length > 0 ? new Date(eventDateArray[0].get('dateFrom').value) : null;
      const lastEventDate = eventDateArray.length > 0 ? new Date(eventDateArray[eventDateArray.length - 1].get('dateFrom').value) : null;

      let endDate: Date | null = lastEventDate;
      let startDate: Date | null = firstEventDate;

      if (repeatEndDate && repeatEndDate.value) {
        endDate = new Date(repeatEndDate.value);
        startDate = firstEventDate; // Ensuring that the first event date is used as the start date
      }

      // Check if taskDate is within the range
      if (startDate && endDate) {
        if (taskDate >= startDate && taskDate <= endDate) {
          return null; // No errors, validation passed
        }
        return {
          dateRange: `${this._translateService.instant('Please set the date between')} ${startDate.toISOString().split('T')[0]} - ${endDate.toISOString().split('T')[0]}.`,
        };
      } else {
        return { dateRange: `${this._translateService.instant('Valid date range is not set. Please check the dates.')}` };
      }
    };
  }

  /**
   * Validates that the repeat end date is after the last event date. If not, an error is returned.
   * All dates are expected to be strings in the format "YYYY-MM-DD".
   * @param form
   * @private
   */

  private _repeatEndDateValidator(form: FormGroup): ValidatorFn {
    return (control: AbstractControl): Record<string, any> | null => {
      const eventDates = form.get('eventDate') as FormArray;
      if (eventDates.length > 0) {
        const lastEventDate = eventDates.at(eventDates.length - 1).get('dateFrom').value;
        const repeatEndDate = control.value;

        // Compare dates
        if (lastEventDate && repeatEndDate && new Date(repeatEndDate) < new Date(lastEventDate)) {
          return { dateRangeInvalid: true };
        }
      }
      return null;
    };
  }
}
